import type { Lang } from '@/i18n';

/**
 * Preuve sociale — LE seul endroit où on la déclare.
 *
 * ⚠️ RÈGLE : rien d'inventé ici. La maquette Claude Design arrivait avec neuf
 * récompenses fictives (« Top 50 Marketing Products 2024 », « GetApp Category
 * Leaders »…), trois témoignages fictifs et une note « 4,9 sur 127 avis »
 * fictive. Tout a été retiré. Chaque tableau vide ci-dessous masque sa section
 * sur la page : mieux vaut une landing plus courte qu'une landing qui ment à
 * des gens qui vont payer.
 *
 * Les neuf badges G2 / Capterra (« Recognized for excellence ») ont été retirés
 * le 01/10/2026 à la demande de Stephen : le 22/09, la liste G2 « Top 50 Sales
 * Products » avait été lue en entier sans que Podawaa ni NeverZero y figurent.
 *
 * Pour remplir : déposer les fichiers dans `public/logos/` et ajouter une
 * entrée. Les chemins sont servis tels quels par Vite, sans
 * passer par le bundler — même URL en SSR et côté client.
 */

export interface ClientLogo {
  name: string;
  /** Optionnel : un logo si on en a un, sinon le nom est affiché en toutes lettres. */
  src?: string;
  /** Optionnel : agrandit un logo au dessin plus fin que les autres (1 = taille normale). */
  scale?: number;
}

export interface Testimonial {
  text: Record<Lang, string>;
  name: string;
  role: Record<Lang, string>;
  /** Optionnel : photo dans `public/`, sinon on retombe sur l'avatar dégradé. */
  avatar?: string;
  /** Étoiles affichées en tête de la carte, sur 5. Absent = pas d'étoiles. */
  stars?: number;
}

export interface Rating {
  /** Note sur 5, telle que la plateforme l'affiche. */
  score: number;
  /** Nombre d'avis réellement comptés. */
  count: number;
  /** D'où vient la note (G2, Capterra…) — jamais « nos clients ». */
  source: string;
  href?: string;
}

/**
 * Bandeau défilant sous le hero. Vide = section masquée.
 *
 * Liste fournie par Stephen le 23/09/2026 (reprise d'un template GoHighLevel) :
 * ce sont des outils et plateformes, pas des clients de NeverZero. C'est pour
 * ça que le libellé au-dessus (`t.trustedBy`) ne dit plus « Trusted by ».
 * ⚠️ Le badge HIPAA est dans la liste à sa demande explicite.
 */
export const CLIENT_LOGOS: ClientLogo[] = [
  { name: 'Printful', src: '/logos/printful.webp' },
  { name: 'Compliancy Group HIPAA', src: '/logos/hipaa-compliancy-group.webp' },
  { name: 'Xero', src: '/logos/xero.webp' },
  { name: 'Zapier', src: '/logos/zapier.webp' },
  { name: 'WhatsApp', src: '/logos/whatsapp.webp' },
  { name: 'Wave', src: '/logos/wave.webp' },
  { name: 'Facebook', src: '/logos/facebook.webp' },
  { name: 'Clio', src: '/logos/clio.webp' },
  { name: 'Stripe', src: '/logos/stripe.webp' },
  { name: 'Shopify', src: '/logos/shopify.webp' },
  { name: 'TikTok', src: '/logos/tiktok.webp' },
  { name: 'QuickBooks', src: '/logos/quickbooks.webp' },
  { name: 'LinkedIn', src: '/logos/linkedin.webp' },
  { name: 'Google', src: '/logos/google.webp' },
  { name: 'Slack', src: '/logos/slack.webp' },
  { name: 'Shippo', src: '/logos/shippo.webp' },
  { name: 'Instagram', src: '/logos/instagram.webp' },
  { name: 'WooCommerce', src: '/logos/woocommerce.webp' },
  { name: 'Google Business Profile', src: '/logos/google-business.webp' },
];

/**
 * « Ils nous font confiance » : de vrais clients, sous le hero. Vide = section
 * masquée. À ne pas confondre avec `CLIENT_LOGOS` (le bandeau d'outils du bas).
 *
 * Liste fournie par Stephen le 02/10/2026. Logos pris sur leurs sites
 * officiels le même jour. N'ajouter ici qu'une entreprise qu'il a confirmée.
 */
export const TRUSTED_BY: ClientLogo[] = [
  { name: 'Waalaxy', src: '/clients/waalaxy.svg' },
  { name: 'lemlist', src: '/clients/lemlist.svg' },
  { name: 'Mailtracker', src: '/clients/mailtracker.webp', scale: 1.3 },
];

/**
 * Section témoignages. Vide = section masquée.
 *
 * Ces deux textes ont été rédigés par Claude le 23/09/2026 puis soumis à leurs
 * auteurs. ✅ VALIDÉS : Stephen confirme le 23/09/2026 que Toinon Georget et
 * Guillaume Portalier ont approuvé chacun le texte publié sous son nom, tel
 * quel. Ce sont de vraies personnes, avec leur nom et leur photo, sur une page
 * qui vend à 199 €/mois : toute modification d'un mot repasse par eux. S'il en
 * retire son accord, on retire son entrée.
 * Avatars recadrés depuis leurs photos de profil, 132 px (3× l'affichage de 44).
 * Les 5 étoiles de chaque carte : demande de Stephen le 02/10/2026.
 */
export const TESTIMONIALS: Testimonial[] = [
  {
    name: 'Toinon Georget',
    role: { en: 'CEO, Waalaxy & Kawaak', fr: 'CEO de Waalaxy & Kawaak' },
    avatar: '/testimonials/toinon-georget.webp',
    stars: 5,
    text: {
      en: "I post on LinkedIn every week, and for too long most of those posts died in silence. With NeverZero, every post gets its first reactions within the hour, and the algorithm finally does the rest. It's the easiest growth lever we've switched on this year.",
      fr: "Je publie sur LinkedIn toutes les semaines, et pendant longtemps la plupart de mes posts mouraient dans le silence. Avec NeverZero, chaque post reçoit ses premières réactions dans l'heure, et l'algorithme fait enfin le reste. C'est le levier de croissance le plus simple qu'on ait activé cette année.",
    },
  },
  {
    name: 'Guillaume Portalier',
    role: { en: 'Co-founder, Waapi', fr: 'Co-fondateur de Waapi' },
    avatar: '/testimonials/guillaume-portalier.webp',
    stars: 5,
    text: {
      en: "We build automation tools for a living, so I'm hard to impress. NeverZero just works: I publish, and my post never sits at zero again. No setup, nothing to think about.",
      fr: "On construit des outils d'automatisation, donc je suis difficile à impressionner. NeverZero fonctionne, tout simplement : je publie, et mon post ne reste plus jamais à zéro. Rien à configurer, rien à surveiller.",
    },
  },
];

/** Note affichée à côté des témoignages. `null` = pas de note affichée. */
export const RATING: Rating | null = null;

/**
 * « 300+ fondateurs nous font déjà confiance » — la maquette l'affichait, et
 * c'est faux : NeverZero n'a pas encore vendu un seul abonnement.
 *
 * Le compteur vit ici, avec le reste de la preuve sociale, et pas dans le
 * dictionnaire i18n : c'est précisément parce qu'il y était que cette phrase a
 * survécu au nettoyage des badges, témoignages et notes fictifs — le mécanisme
 * « tableau vide = section masquée » ne voyait pas cette chaîne-là.
 *
 * `null` masque le bloc entier (avatars compris). Y mettre un nombre, c'est
 * affirmer ce nombre à des gens qui vont payer 199 €/mois : il se compte, il ne
 * s'estime pas.
 *
 * ✅ DÉCISION DE STEPHEN, 23/09/2026 : 10 000, la communauté Podawaa dont vient
 * le réseau qui engage. Ne pas le changer sans lui.
 */
export const JOIN_COUNT: number | null = 10000;

/**
 * Les fondateurs, eux, sont réels : ce sont les deux visages de l'agence, en
 * haut de page et dans la section équipe.
 *
 * Portraits recadrés depuis la photo de groupe d'origine (1240×698, ex-
 * `founders.webp`), en 416×520 : le double de la plus grande taille affichée,
 * pour les écrans 2×. WebP avec repli JPEG — une photo qui ne s'affiche pas sur
 * une page de conversion ne se rattrape pas.
 *
 * Rien d'autre sur eux tant qu'ils ne l'ont pas écrit eux-mêmes : pas de bio.
 * `linkedin` n'est pas deviné : ce sont les profils capturés dans
 * `backend-lib/tests/linkedin-profiles/` (stephen.html, tom.html ; tom.html est
 * capturé connecté en Stephen, d'où son `publicIdentifier` dans la page). Il ne
 * sert qu'au `sameAs` du JSON-LD, qui relie l'auteur des articles à son profil.
 */
export interface Founder {
  name: string;
  firstName: string;
  role: { en: string; fr: string };
  photo: { webp: string; fallback: string };
  linkedin: string;
}

export const FOUNDER_PHOTO_SIZE = { width: 416, height: 520 } as const;

export const FOUNDERS: Founder[] = [
  {
    name: 'Stephen Zambaux',
    firstName: 'Stephen',
    role: { en: 'Co-founder & CTO', fr: 'Co-fondateur & CTO' },
    photo: { webp: '/team/stephen.webp', fallback: '/team/stephen.jpg' },
    linkedin: 'https://www.linkedin.com/in/stephen-zambaux',
  },
  {
    name: 'Tom Benattar',
    firstName: 'Tom',
    role: { en: 'Co-founder & CEO', fr: 'Co-fondateur & CEO' },
    photo: { webp: '/team/tom.webp', fallback: '/team/tom.jpg' },
    linkedin: 'https://www.linkedin.com/in/tom-benattar',
  },
];
